import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreditCard, FileText, CheckCircle, Shield, Building2 } from 'lucide-react';
import { useAgreement } from '../context/AgreementContext';
import { getXeroPaymentUrl, getAgreementById } from '../services/agreementService';

export function PaymentOptions() {
  const { state, dispatch } = useAgreement();
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch agreement data when component mounts to ensure we have the latest data
  useEffect(() => {
    const fetchAgreementData = async () => {
      if (!agreement_id) return;
      
      setIsLoading(true);
      try {
        console.log('PaymentOptions: Fetching fresh agreement data for ID:', agreement_id);
        const agreementData = await getAgreementById(agreement_id);
        
        if (agreementData) {
          console.log('PaymentOptions: Loaded agreement data:', agreementData);
          // Update the context with the fetched data
          dispatch({ type: 'LOAD_AGREEMENT', payload: agreementData });
        }
      } catch (error) {
        console.error('PaymentOptions: Error fetching agreement data:', error);
        // Don't show error to user, just log it - the component will show fallback text
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgreementData();
  }, [agreement_id, dispatch]);

  // Get fee values from agreement data, no fallback prices
  const retainerFeeExGST = state.total_retainer;
  const successFeeExGST = state.total_fee && state.total_retainer ? (state.total_fee - state.total_retainer) : undefined;
  const invoiceId = state.xero_invoice_number;
  
  // Add GST (multiply by 1.1) to get the total amount including GST
  const retainerFee = retainerFeeExGST ? retainerFeeExGST * 1.1 : 0;
  const successFee = successFeeExGST ? successFeeExGST * 1.1 : 0;
  const totalFee = retainerFee + successFee;
  
  // Helper function to display fee or fallback message
  const displayFeeOrFallback = (fee: number) => {
    return fee > 0 ? formatPrice(fee) : "Request Property Consultant Assistance";
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handlePayNow = async () => {
    setIsProcessing(true);
    
    try {
      if (!agreement_id) {
        throw new Error('Agreement ID not found');
      }

      // Get the Xero payment URL for this agreement
      console.log('Getting Xero payment URL for agreement:', agreement_id);
      const response = await getXeroPaymentUrl(agreement_id);
      
      if (response.success && response.payment_url) {
        console.log('Redirecting to Xero payment URL:', response.payment_url);
        // Redirect to Xero payment page in the same tab
        window.location.href = response.payment_url;
      } else {
        console.error('Failed to get Xero payment URL:', response.error);
        // Fallback: show error message or redirect to confirmation
        alert(`Unable to redirect to payment page: ${response.error || 'Unknown error'}`);
        navigate(`/${agreement_id}/confirmation`);
      }
    } catch (error) {
      console.error('Payment redirection failed:', error);
      // Fallback: show error message or redirect to confirmation
      alert(`Unable to redirect to payment page: ${error instanceof Error ? error.message : 'Unknown error'}`);
      navigate(`/${agreement_id}/confirmation`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToAgreement = () => {
    // Pass state to indicate this is a manual back navigation
    navigate(`/${agreement_id}/start-agreement`, {
      state: { fromBackButton: true, skipRedirect: true }
    });
  };

  // Show loading state while fetching agreement data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Payment & Confirmation
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Loading payment information...
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Payment & Confirmation
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Complete your payment to activate your property search service
          </p>
          <button
            onClick={handleBackToAgreement}
            className="mt-4 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Agreement
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Payment Summary
            </h2>

            <div className="space-y-4 mb-4 sm:mb-6">
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <div>
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Retainer Fee</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Initial service fee (payable now)</p>
                </div>
                <span className="text-base sm:text-lg font-semibold text-gray-900">
                  {displayFeeOrFallback(retainerFee)}
                </span>
              </div>

              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <div>
                  <h3 className="font-medium text-gray-900 text-sm sm:text-base">Success Fee</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Fixed fee (payable at settlement)</p>
                </div>
                <span className="text-sm text-gray-600">
                  {displayFeeOrFallback(successFee)}
                </span>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-blue-900 text-sm sm:text-base">Total Due Now</span>
                  <span className="text-lg sm:text-xl font-bold text-blue-900">
                    {displayFeeOrFallback(retainerFee)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500 space-y-1">
              <p>* Success fee only applies if we successfully find and help you purchase a property</p>
              <p>** All prices include GST</p>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Payment Methods
            </h2>

            {/* Pay by Card Now Button */}
            <button
              onClick={handlePayNow}
              disabled={isProcessing || retainerFee <= 0}
              className={`w-full py-3 px-6 rounded-full font-semibold text-base transition-all duration-200 flex items-center justify-center gap-3 min-h-[44px] mb-6 ${
                isProcessing || retainerFee <= 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-500 hover:bg-primary-600 text-white'
              }`}
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing Payment...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Pay by Card Now - {displayFeeOrFallback(retainerFee)}
                </>
              )}
            </button>

            {retainerFee <= 0 && (
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-center text-sm text-yellow-800">
                  <strong>Fee information not available.</strong><br />
                  Please contact your Property Consultant for assistance with payment details.
                </p>
              </div>
            )}

            {/* Or Divider */}
            <div className="flex items-center mb-6">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-sm text-gray-500 bg-white">Or</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* Bank Transfer Option */}
            <div className="border-2 border-gray-200 bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <Building2 className="w-5 h-5 text-gray-600" />
                <h3 className="font-medium text-gray-900 text-sm sm:text-base">Pay by Bank Transfer</h3>
              </div>
              
              <div className="space-y-2 text-xs sm:text-sm text-gray-700">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Account Name:</span>
                  </div>
                  <div>
                    Search Property Pty Ltd
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">BSB:</span>
                  </div>
                  <div>
                    062-692
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Account Number:</span>
                  </div>
                  <div>
                  4057 4574
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Reference:</span>
                  </div>
                  <div>
                    {invoiceId}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">Amount:</span>
                  </div>
                  <div className="font-semibold">
                    {displayFeeOrFallback(retainerFee)} AUD
                  </div>
                </div>
              </div>
              
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                <strong>Important:</strong> Please include the reference number when making your transfer.
                Your service will be activated once payment is received and verified.
              </div>
            </div>

            {retainerFee > 0 && (
              <p className="text-center text-xs text-gray-500 mt-4">
                You will receive email confirmation once payment is processed
              </p>
            )}
          </div>
        </div>

        {/* Service Confirmation */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 md:p-8 mt-6 sm:mt-8">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
            What Happens Next?
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Immediate Confirmation</h3>
              <p className="text-xs sm:text-sm text-gray-600">You'll receive email confirmation with your signed agreement and payment receipt</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Service Activation</h3>
              <p className="text-xs sm:text-sm text-gray-600">Our team will contact you within 24 hours to begin your property search</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">Regular Updates</h3>
              <p className="text-xs sm:text-sm text-gray-600">Receive weekly market insights and property opportunities matching your criteria</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}